import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { apiFetch, assetUrl } from "../utils/api";
import { EventRichContent, EventStatusBadge, formatEventTime } from "./EventsPage";

function buildOgDescription(item) {
  const parts = [item.summary || "线下活动报名中，欢迎报名参加"];
  if (item.city) parts.push(item.city);
  if (item.start_at) parts.push(item.start_at);
  parts.push("点开即可查看详情并报名");
  return parts.join("｜").slice(0, 80);
}

function buildShareText(item, url) {
  const place = [item.city, item.venue].filter(Boolean).join(" · ") || "详见活动页";
  return [
    `【线下活动】${item.title}`,
    "",
    item.summary || "欢迎报名参加这次线下见面活动。",
    "",
    `时间：${formatEventTime(item.start_at, item.end_at)}`,
    `地点：${place}`,
    item.fee_label ? `费用：${item.fee_label}` : "",
    "",
    "点开报名：",
    url,
    "",
    "婚恋有道 · 认真相亲，线下见面",
  ]
    .filter((line, index, arr) => !(line === "" && arr[index - 1] === ""))
    .join("\n")
    .trim();
}

function copyTextFallback(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  const ok = document.execCommand("copy");
  document.body.removeChild(textarea);
  return ok;
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareHint, setShareHint] = useState("");
  const [showShareText, setShowShareText] = useState(true);

  const pagePath = `/events/${eventId}`;
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${pagePath}`
      : pagePath;

  useSeo(
    item
      ? {
          title: `${item.title} - 线下活动`,
          metaDescription: buildOgDescription(item),
          ogDescription: buildOgDescription(item),
          keywords: "线下活动,相亲会,联谊,情感沙龙",
          path: pagePath,
          ogImage: item.cover_url || "",
        }
      : { title: "线下活动", path: pagePath, noindex: true }
  );

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/public/events/${eventId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.detail) throw new Error(data.detail);
        setItem(data);
      })
      .catch((err) => setError(err.message || "活动不存在"))
      .finally(() => setLoading(false));
  }, [eventId]);

  const shareText = item ? buildShareText(item, shareUrl) : "";

  const copyShareText = async () => {
    if (!shareText) return;
    setShowShareText(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        setShareHint("广告文案已复制。打开微信发给好友时直接粘贴，对方就能看到标题和链接。");
        return;
      }
    } catch {
      /* 微信内置浏览器常禁用 clipboard，改用备用方式 */
    }
    if (copyTextFallback(shareText)) {
      setShareHint("广告文案已复制。打开微信发给好友时直接粘贴即可。");
      return;
    }
    setShareHint("请长按下方文案全选复制，再到微信粘贴发给好友。");
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareHint("链接已复制。发给好友时建议改用「复制广告文案」，对方能直接看到标题。");
        return;
      }
    } catch {
      /* ignore */
    }
    if (copyTextFallback(shareUrl)) {
      setShareHint("链接已复制");
      return;
    }
    setShareHint("请手动复制浏览器地址栏链接");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-10">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="bg-white p-10 rounded-2xl shadow text-center text-gray-400">加载中...</div>
          ) : error || !item ? (
            <div className="bg-white p-10 rounded-2xl shadow text-center space-y-4">
              <p className="text-gray-500">{error || "活动不存在"}</p>
              <Link to="/events" className="text-pink-600 hover:underline">
                返回活动列表
              </Link>
            </div>
          ) : (
            <article className="bg-white rounded-3xl shadow-lg overflow-hidden">
              {item.cover_url ? (
                <img
                  src={assetUrl(item.cover_url)}
                  alt={item.title}
                  className="w-full h-56 md:h-72 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-r from-pink-400 to-rose-400 flex items-center justify-center text-white text-5xl">
                  🎪
                </div>
              )}
              <div className="p-7 md:p-12 space-y-8">
                <Link to="/events" className="text-sm text-pink-500 hover:underline">
                  ← 全部线下活动
                </Link>
                <header className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm tracking-wide text-pink-500">{item.category_label}</span>
                    <EventStatusBadge status={item.status} label={item.status_label} />
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-800 leading-snug tracking-wide">
                    {item.title}
                  </h1>
                  {item.summary ? (
                    <p className="text-gray-600 text-[16px] leading-[2] tracking-wide">
                      {item.summary}
                    </p>
                  ) : null}
                  <div className="rounded-2xl border border-green-100 bg-green-50/70 p-5 space-y-3">
                    <p className="text-sm text-gray-700 leading-7">
                      发给微信好友请复制下面这段广告文案再粘贴。朋友圈可以直接转发链接（会显示标题）。
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={copyShareText}
                        className="px-5 py-2.5 rounded-full bg-green-500 text-white text-sm font-medium hover:bg-green-600"
                      >
                        复制广告文案
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="px-5 py-2.5 rounded-full border border-pink-300 text-pink-600 text-sm bg-white hover:bg-pink-50"
                      >
                        只复制链接
                      </button>
                    </div>
                    {shareHint ? (
                      <p className="text-sm text-green-800 leading-7">{shareHint}</p>
                    ) : null}
                    {showShareText ? (
                      <textarea
                        readOnly
                        value={shareText}
                        onFocus={(e) => e.target.select()}
                        className="w-full min-h-[180px] text-[15px] leading-8 text-gray-700 bg-white border border-green-100 rounded-xl px-4 py-3"
                      />
                    ) : null}
                  </div>
                </header>

                <div className="grid sm:grid-cols-2 gap-4 text-[15px] leading-8 bg-rose-50 rounded-2xl p-5 md:p-6 text-gray-700">
                  <p>时间：{formatEventTime(item.start_at, item.end_at)}</p>
                  <p>城市：{item.city || "待定"}</p>
                  <p>地点：{item.venue || "待定"}</p>
                  <p>费用：{item.fee_label || "免费"}</p>
                  {item.address ? <p className="sm:col-span-2">地址：{item.address}</p> : null}
                  {item.quota_label ? <p>名额：{item.quota_label}</p> : null}
                </div>

                {item.content ? (
                  <section className="pt-2 border-t border-rose-100">
                    <h2 className="text-lg font-semibold text-gray-800 tracking-wide mb-6">
                      活动详情
                    </h2>
                    <EventRichContent content={item.content} />
                  </section>
                ) : null}

                <section className="rounded-2xl border border-pink-100 bg-pink-50/40 p-6 md:p-7 space-y-3">
                  <h2 className="font-bold text-gray-800 tracking-wide">报名方式</h2>
                  {item.contact_wechat ? (
                    <p className="text-[15px] leading-8 text-gray-700">微信：{item.contact_wechat}</p>
                  ) : null}
                  {item.contact_phone ? (
                    <p className="text-[15px] leading-8 text-gray-700">电话：{item.contact_phone}</p>
                  ) : null}
                  {item.signup_note ? (
                    <p className="text-[15px] leading-[2] tracking-wide text-gray-600 whitespace-pre-line">
                      {item.signup_note}
                    </p>
                  ) : null}
                  {!item.contact_wechat && !item.contact_phone && !item.signup_note ? (
                    <p className="text-[15px] leading-8 text-gray-400">报名方式稍后公布，请关注本页更新。</p>
                  ) : null}
                </section>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}